import { HttpClient, HttpHeaders, HttpRequest, HttpResponse
       } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { ProductDesignerComponentConfig
       } from 'app/product-designer-component-config';
import { ProductConfig } from 'app/product-designer-config';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { ConfigService } from 'core/config/config.service';
import { TrivialError } from 'core/error/error';
import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';

interface StructureInfo
{
  structure: string;
  template_group: string;
  template: string;
}

export class BuildPostDataError extends TrivialError
{
  protected _name: string = 'BuildPostDataError';
}

export class PostToCartError extends TrivialError
{
  protected _name: string = 'PostToCartError';
}

export interface PostedProducts {
  redirect_location?: string;
  products: Array<PostedProduct>;
}

export interface PostedProduct {
  config_url: string;
  quantity: number;
}

@Injectable()
export class ProductdesignerdService
{
  constructor(
      private http: HttpClient,
      @Inject(ConfigService) private configService: ConfigService,
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(LoggingService) private loggingService: LoggingService,
  )
  { }

  public async postProducts(
      productDesignerComponentConfig: ProductDesignerComponentConfig,
      productConfig: ProductConfig): Promise<PostedProducts>
  {
    return new Promise<PostedProducts>(async (resolve, reject) => {
      try {
        let postData = this.buildPostData(
            productDesignerComponentConfig.productStoreURL,
            productDesignerComponentConfig.catalogStoreId,
            productDesignerComponentConfig.catalogRevision,
            productDesignerComponentConfig.locale,
            productConfig);

        const req = new HttpRequest('POST', this.designResultsURL,
            postData, { headers: new HttpHeaders() });

        const resp = await this.http.request(req)
          .pipe(Utils.retryPipe())
          .toPromise()
          .catch((error) => {
            throw error;
          });

        if (resp instanceof HttpResponse) {
          if (resp.status >= 200 && resp.status < 300) {
            const products = (resp.body['products'] || []).map(p =>
                <PostedProduct>{
                    config_url: p['config-url'],
                    quantity: p['quantity']
                });
            let redirect_location = resp.body['redirect_target_url'];
            if (productDesignerComponentConfig.cartURL) {
              redirect_location =
                  await this.postToCart(
                      productDesignerComponentConfig.cartURL, products);
            }
            resolve(<PostedProducts>{
                redirect_location: redirect_location,
                products: products
            });
          }
          else {
            reject(resp.status);
          }
        }
      }
      catch (error) {
        reject(error);
      }
    });
  }

  public queryHeartbeat(): Promise<boolean>
  {
    return new Promise<boolean>((resolve, reject) => {
      if (!this.heartbeatURL) {
        return resolve(true);
      }

      this.http
        .head(this.heartbeatURL)
        .subscribe(_ => resolve(true), _ => resolve(false));
    });
  }

  public get heartbeatURL(): string
  {
    return this.configService.getProductDesignerComponentConfig().heartbeatURL;
  }

  public get designResultsURL(): string
  {
    return this.configService
      .getProductDesignerComponentConfig().designResultsURL;
  }

  private buildPostData(
      productStoreURL: string,
      catalogStoreId: string,
      catalogRevision: string,
      locale: string,
      productConfig: ProductConfig): any
  {
    let postData = {
        product_store_url: productStoreURL,
        catalog_store_id: catalogStoreId,
        catalog_revision: catalogRevision,
        locale: locale,
        products: [ ],
        logs: this.loggingService.messages
          .filter(m => [Severity.WARNING, Severity.ERROR].includes(m.severity))
          .map(m => `${m.severity}: ${m.origin}: ${m.message}`),
    };
    postData.products.push(this.getProductPostData(productConfig));
    for (let additionalProductId of productConfig.selectedAdditionalProducts) {
      for (let additionalProductConfig of productConfig.additionalProducts) {
        if (additionalProductConfig.id === additionalProductId) {
          postData.products.push(this.getProductPostData(
              additionalProductConfig));
          break;
        }
      }
    }
    return postData;
  }

  /**
   * @throws PostToCartError
   */
  private async postToCart(cartURL: string,
                           products: Array<PostedProduct>
                           ): Promise<string|undefined>
  {
    const postData = {
        products: products.map(p => {
          return {
              'config-url': p.config_url,
              'quantity': p.quantity
          };
        })
    };
    const req = new HttpRequest(
        'POST', cartURL, postData, { headers: new HttpHeaders() });
    const resp = await this.http.request(req)
      .toPromise()
      .catch((error) => {
        throw new PostToCartError(error.toString());
      });
    if (resp instanceof HttpResponse) {
      if (resp.status >= 200 && resp.status < 300) {
        const location = resp.headers.get('Location');
        return location ? location : undefined;
      }
      else {
        throw new PostToCartError('invalid status code: ' + resp.status);
      }
    }
    else {
      throw new PostToCartError('invalid response type');
    }
  }

  /**
   * @throws BuildPostDataError
   */
  private getProductPostData(productConfig: ProductConfig): any
  {
    const productStoreProductType = productConfig.collageDesignerConfig
        ? productConfig.collageDesignerConfig.productStoreProductType
        : undefined;

    let productPostData = {
        catalog_product_id: productConfig.id,
        configuration_id: productConfig.configurationId,
        product_store_product_type: productStoreProductType,
        quantity: productConfig.quantity,
        parts: this.getProductParts(productConfig.collageDesignerConfig),
        feature_selections: this.getProductFeatureSelections(productConfig),
    };
    if (productConfig.productStoreURL) {
      productPostData['product_store_url'] = productConfig.productStoreURL;
    }

    return productPostData;
  }

  /**
   * @throws BuildPostDataError
   */
  private getProductParts(collageDesignerConfig: any): any
  {
    let parts = {};
    if (collageDesignerConfig) {
      for (let stepId of Object.keys(collageDesignerConfig.steps)) {
        for (let part of collageDesignerConfig.steps[stepId].parts) {
          const structureInfo = this.getStructureInfo(
            collageDesignerConfig.steps[stepId], parseInt(stepId, 10), part);
          if (structureInfo) {
            parts[part.id] = structureInfo;
          }
        }
      }
    }

    return parts;
  }

  /**
   * @throws BuildPostDataError
   */
  private getStructureInfo(
    stepConfig: any, stepId: number, part: any): StructureInfo | undefined
  {
    const editingViewType = part.editingView.type;

    const lookupStructure =
      (selectedPart: any, selectedNodeTag?: string): StructureInfo => {
        const designState =
          this.designStepService.getDesignState(stepId, selectedPart.id);

        if (!designState) {
          return undefined;
        }

        const rawStructure = Utils
          .find(selectedPart.templateGroups,
                g => g.id === designState.templateGroupId)
          .templates[designState.templateId]
          .stageConfig
          .svgStructure;

        return {
          // IE11-Hack: Der IE wirft den pap-namespace aus Unter-SVGs beim POST
          structure: StageUtils.ensureHasPapNamespace(
            StageUtils.sanitizeStructure(
              rawStructure, true, true, false, selectedNodeTag)),
          template_group: designState.templateGroupId,
          template: designState.templateId,
        };
      };

    switch (editingViewType) {
      case 'stage': // fall through to 'default'
      case 'default': return lookupStructure(part);

      case 'reference': {
        const stagePart =
          stepConfig.parts.filter(p => p.editingView.type === 'stage')[0];

        return lookupStructure(
          stagePart, part.editingView.reference || part.id);
      }

      default: {
        const message = 'invalid `editing_view.type`: ' + editingViewType;
        this.loggingService.log(
            'ProductdesignerdService', Severity.ERROR, message);
        throw new BuildPostDataError(message);
      }
    }
  }

  private getProductFeatureSelections(productConfig: ProductConfig): any
  {
    let features = {};
    for (let featureGroupId of Object.keys(productConfig.featureGroups)) {
      features[featureGroupId] =
          productConfig.featureGroups[featureGroupId].selectedFeature;
    }
    return features;
  }
}
